<template>
  <div id="creditCardModal" class="uk-modal" uk-modal>
    <div class="uk-modal-dialog uk-modal-body modal-container">
      <button class="uk-modal-close-default" type="button" uk-close></button>

      <div class="form-row">
        <!-- Credit Card Form -->
        <div class="form-col">
          <h4>{{ $t('general.payment_by_credit_card') }}</h4>

          <div class="form-group">
            <label>{{ $t('general.name_surname_on_the_card') }}</label>
            <input v-model="cardNameSurname" class="form-control" type="text">
          </div>

          <div class="form-group">
            <label>{{ $t('general.credit_card_number') }}</label>
            <div class="input-group">
              <span class="input-group-text"><i :class="cardBrandClass"></i></span>
              <input ref="cardNumInput"
                     v-model="cardNumber"
                     v-cardformat:formatCardNumber
                     class="form-control"
                     placeholder="#### #### #### ####"
                     type="tel">
            </div>
            <small v-if="cardErrors.cardNumber" class="error-text">
              <i class="uil-times"></i> {{ cardErrors.cardNumber }}
            </small>
          </div>

          <div class="row">
            <div class="col-6">
              <div class="form-group">
                <label>{{ $t('general.expiration_date') }}</label>
                <input ref="cardExpInput"
                       v-model="cardExpiry"
                       v-cardformat:formatCardExpiry
                       class="form-control"
                       placeholder="MM/YY">
                <small v-if="cardErrors.cardExpiry" class="error-text">
                  <i class="uil-times"></i> {{ cardErrors.cardExpiry }}
                </small>
              </div>
            </div>
            <div class="col-6">
              <div class="form-group">
                <label>{{ $t('general.security_code_cvv') }}</label>
                <input ref="cardCvcInput"
                       v-model="cardCvc"
                       v-cardformat:formatCardCVC
                       class="form-control"
                       placeholder="CVC">
                <small v-if="cardErrors.cardCvc" class="error-text">
                  <i class="uil-times"></i> {{ cardErrors.cardCvc }}
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="form-col order-col">
          <h4>{{ $t('general.my_purchases') }}</h4>
          <ul class="list-group">
            <li v-for="(item, idx) in orderedItemListForBuying.order_data"
                :key="idx"
                class="list-group-item d-flex justify-content-between">
              <span>{{ item.title }}</span>
              <span>{{ formatPrice(item.price, item.currency) }}</span>
            </li>
            <li v-if="orderedItemListForBuying.total"
                class="list-group-item d-flex justify-content-between total-row">
              <strong>{{ $t('general.total_vat_included') }}</strong>
              <strong>{{ formatPrice(orderedItemListForBuying.total, orderedItemListForBuying.currency) }}</strong>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="errorCardMessage" class="uk-text-danger error-message">
        <i class="uil-exclamation-triangle"></i> {{ errorCardMessage }}
      </div>

      <div class="modal-footer">
        <button class="btn btn-light" type="button" @click="reset">
          {{ $t('general.clear_all') }}
        </button>
        <div class="footer-right">
          <button class="btn btn-outline-secondary uk-modal-close" type="button">
            <i class="uil-times"></i> {{ $t("general.close") }}
          </button>
          <button class="btn btn-primary" type="button" @click="submitCreditCard">
            {{ $t('general.send') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CreditCardModal",
  props: {
    formatPrice: {
      type: Function,
      required: true
    },
    orderedItemListForBuying: {
      type: Object,
      default: () => ({})
    },
    token: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      errorCardMessage: '',
      cardNameSurname: '',
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      cardPostal: null,
      cardErrors: {},
      cardBrand: null,
    };
  },
  computed: {
    cardBrandClass() {
      return this.getBrandClass(this.cardBrand);
    },
    apiHeaders() {
      return this.$store.getters['auth/apiHeaders'];
    }
  },
  watch: {
    cardNumber: function (val) {
      if (this.$cardFormat && this.$cardFormat.validateCardNumber(val)) {
        this.$refs.cardExpInput.focus();
      }
    },
    cardExpiry: function (val) {
      if (this.$cardFormat && this.$cardFormat.validateCardExpiry(val)) {
        this.$refs.cardCvcInput.focus();
      }
    },
  },
  methods: {
    validate: function () {
      this.cardErrors = {};

      if (this.$cardFormat && !this.$cardFormat.validateCardNumber(this.cardNumber)) {
        this.cardErrors.cardNumber = "Geçersiz Kredi Kartı Numarası.";
      }

      if (this.$cardFormat && !this.$cardFormat.validateCardExpiry(this.cardExpiry)) {
        this.cardErrors.cardExpiry = "Geçersiz Son Kullanma Tarihi.";
      }

      if (this.$cardFormat && !this.$cardFormat.validateCardCVC(this.cardCvc)) {
        this.cardErrors.cardCvc = "Geçersiz CVC.";
      }
    },
    reset: function () {
      this.cardErrors = {};
      this.cardNumber = null;
      this.cardExpiry = null;
      this.cardCvc = null;
      this.cardPostal = null;
      this.cardNameSurname = '';
      if (this.$refs.cardNumInput) {
        this.$refs.cardNumInput.focus();
      }
    },
    getBrandClass: function (brand) {
      let icon = '';
      brand = brand || 'unknown';
      let cardBrandToClass = {
        'visa': 'fab fa-cc-visa',
        'mastercard': 'fab fa-cc-mastercard',
        'amex': 'fab fa-cc-amex',
        'discover': 'fab fa-cc-discover',
        'diners': 'fab fa-cc-diners-club',
        'jcb': 'fab fa-cc-jcb',
        'unknown': 'fa fa-credit-card',
      };
      if (cardBrandToClass[brand]) {
        icon = cardBrandToClass[brand];
      }

      return icon;
    },
    submitCreditCard() {
      this.validate();

      if (Object.keys(this.cardErrors).length > 0) {
        return;
      }

      let [month, year] = this.cardExpiry.replace(/\s/g, "").split("/");
      year = "20" + year;

      const cleanCardNumber = this.cardNumber.replace(/\s/g, "");

      axios.post(
          "api/payment/" + this.token + "/pay",
          {
            "credit_card": {
              "name_surname": this.cardNameSurname,
              "number": cleanCardNumber,
              "month": month,
              "year": year,
              "cvc": this.cardCvc,
            },
          },
          {
            headers: this.apiHeaders,
          }
      ).then((result) => {
        this.errorCardMessage = ''
        if (result.data.status) {
          window.location.replace(result.data.url);
        } else {
          if (result?.data?.message)
            this.errorCardMessage = result?.data?.message
        }
      });
    },
    show() {
      UIkit.modal("#creditCardModal").show();
    }
  }
}
</script>

<style scoped>
.modal-container {
  width: 700px;
  max-width: 95vw;
  border-radius: 10px;
  padding: 24px;
  background: #f8f8f8;
}

h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

/* Form Row - 2 columns */
.form-row {
  display: flex;
  gap: 24px;
}

.form-col {
  flex: 1;
  min-width: 0;
}

.order-col {
  flex: 0 0 240px;
}

/* Form Elements */
.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #555;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: var(--backgroundColor, #667eea);
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

/* Input Group */
.input-group {
  display: flex;
  align-items: stretch;
}

.input-group-text {
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 6px 0 0 6px;
  font-size: 18px;
  color: #666;
}

.input-group .form-control {
  border-radius: 0 6px 6px 0;
  flex: 1;
}

/* Row for expiry and CVC */
.row {
  display: flex;
  gap: 12px;
}

.col-6 {
  flex: 1;
}

/* Order List */
.list-group {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.list-group-item {
  padding: 12px;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.list-group-item:last-child {
  border-bottom: none;
}

.total-row {
  background: #f9f9f9;
  border-top: 2px solid #ddd;
}

/* Error */
.error-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #e53e3e;
}

.error-message {
  margin-top: 16px;
  padding: 10px 12px;
  background: #fff5f5;
  border-radius: 6px;
  font-size: 13px;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #ddd;
}

.footer-right {
  display: flex;
  gap: 10px;
}

.modal-footer .btn {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;
}

.btn-light {
  background: #fff;
  border: 1px solid #ddd;
  color: #666;
}

.btn-light:hover {
  background: #f5f5f5;
}

.btn-outline-secondary {
  background: #fff;
  border: 1px solid #ddd;
  color: #666;
}

.btn-outline-secondary:hover {
  background: #f5f5f5;
}

.btn-primary {
  background: var(--backgroundColor, #667eea);
  border: none;
  color: #fff;
}

.btn-primary:hover {
  opacity: 0.9;
}

/* ==========================================================================
   Responsive - Tablet
   ========================================================================== */
@media (max-width: 768px) {
  .modal-container {
    padding: 20px;
  }

  .form-row {
    flex-direction: column;
    gap: 20px;
  }

  .order-col {
    flex: none;
    order: -1;
  }

  .modal-footer {
    flex-direction: column;
    gap: 12px;
  }

  .modal-footer .btn {
    width: 100%;
  }

  .footer-right {
    width: 100%;
    flex-direction: column;
  }
}

/* ==========================================================================
   Responsive - Mobile
   ========================================================================== */
@media (max-width: 480px) {
  .modal-container {
    padding: 16px;
  }

  h4 {
    font-size: 15px;
  }

  .row {
    flex-direction: column;
    gap: 0;
  }

  .col-6 {
    flex: none;
  }

  .form-control {
    padding: 12px;
    font-size: 16px;
  }

  .list-group-item {
    padding: 10px;
    font-size: 12px;
  }

  .modal-footer .btn {
    padding: 12px 16px;
  }
}

@media (max-width: 768px) {
  .modal-container {
    width: calc(100vw - 24px);
    max-width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
    box-sizing: border-box;
    border-radius: 12px;
  }

  .form-control,
  .input-group-text {
    min-height: 48px;
    box-sizing: border-box;
    font-size: 16px;
  }

  .modal-footer .btn {
    min-height: 48px;
    box-sizing: border-box;
  }
}
</style>
